/**
 * Parses CSV file content to extract carousel configuration data with ranks.
 *
 * @param {string} fileText - The raw CSV file content as a string
 * @returns {Array<Object>} Array of parsed entries with the following structure:
 *   - carouselID: string - The carousel identifier from the CSV
 *   - rank: number|null - The numeric rank (1-based) if provided, null if missing or invalid
 *   - sku: string - The external SKU identifier
 * @throws {Error} Throws an error if required columns "Carousel ID" or "External SKU" are missing
 *
 * @example
 * const csvText = "Carousel ID,Rank,External SKU\ncarousel1,1,SKU123\ncarousel1,,SKU456";
 * const result = parseCSVWithRanks(csvText);
 * // Returns: [
 * //   { carouselID: 'carousel1', rank: 1, sku: 'SKU123' },
 * //   { carouselID: 'carousel1', rank: null, sku: 'SKU456' }
 * // ]
 */
export function parseCSVWithRanks(fileText) {
    const rows = fileText.trim().replace(/\r/g, '').split(`\n`);
    const headers = rows[0].split(',').map((h) => h.trim());

    const carouselIdx = headers.indexOf('Carousel ID');
    const rankIdx = headers.indexOf('Rank');
    const skuIdx = headers.indexOf('External SKU');

    if (carouselIdx === -1 || skuIdx === -1) {
        throw new Error('CSV must include "Carousel ID" and "External SKU" columns.');
    }

    const parsed = rows.slice(1).map((row) => {
        const cols = row.split(',').map((c) => c.trim());
        return {
            carouselID: cols[carouselIdx],
            rank: rankIdx !== -1 && cols[rankIdx] ? Number(cols[rankIdx]) : null,
            sku: cols[skuIdx],
        };
    });

    return parsed;
}

/**
 * Orders items by their rank, placing ranked items first in order, followed by unranked items.
 *
 * @param {Array<Object>} entries - Array of parsed CSV entries from parseCSVWithRanks
 * @param {Object} itemMap - Map/object where keys are SKUs and values are item objects to be ordered
 * @returns {Array} Ordered array of items, with ranked items positioned by their rank (1-based),
 *                  followed by unranked items appended at the end. Gaps in ranking are filtered out.
 *
 * @example
 * const entries = [
 *   { carouselID: 'c1', rank: 2, sku: 'SKU1' },
 *   { carouselID: 'c1', rank: 1, sku: 'SKU2' },
 *   { carouselID: 'c1', rank: null, sku: 'SKU3' }
 * ];
 *
 * const itemMap = {
 *   'SKU1': { id: 1, name: 'Item 1', ... },
 *   'SKU2': { id: 2, name: 'Item 2', ... },
 *   'SKU3': { id: 3, name: 'Item 3', ... }
 * };
 *
 * const result = orderItemsByRank(entries, itemMap);
 * // Returns: [
 * //   { id: 2, name: 'Item 2', ... },  // rank 1
 * //   { id: 1, name: 'Item 1', ... },  // rank 2
 * //   { id: 3, name: 'Item 3', ... }   // unranked, appended
 * // ]
 */
export function orderItemsByRank(entries, itemMap) {
    const ranked = entries.filter((e) => e.rank != null && !isNaN(e.rank)).sort((a, b) => a.rank - b.rank);

    const unranked = entries.filter((e) => e.rank == null || isNaN(e.rank));

    const ordered = [];

    ranked.forEach((e) => {
        const item = itemMap[e.sku];
        if (item) ordered[e.rank - 1] = item;
    });

    unranked.forEach((e) => {
        const item = itemMap[e.sku];
        if (item) ordered.push(item);
    });

    return ordered.filter(Boolean); // Remove gaps
}
